import {
  sendSignal,
  startAuthentication,
  startRegistration,
  WebAuthnError,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import {
  CeremonyOptions,
  OkResponse,
  UserResponse,
  type PasskeyFailureReport,
  type RegisterOptionsRequest,
  type User,
} from '@mtg/shared';
import { api, ApiError } from './api.ts';

/** Sends the browser's error for a failed passkey prompt to the server log, then rethrows it. */
function reportFailure(context: PasskeyFailureReport['context']) {
  return (e: unknown): never => {
    const err = e instanceof Error ? e : new Error(String(e));
    const cause = err.cause instanceof Error ? `${err.cause.name}: ${err.cause.message}` : undefined;
    const report: PasskeyFailureReport = {
      context,
      name: err.name.slice(0, 100),
      message: err.message.slice(0, 500),
      code: e instanceof WebAuthnError ? e.code : undefined,
      cause: cause?.slice(0, 300),
    };
    void api('/api/auth/client-error', OkResponse, { body: report }).catch(() => {});
    throw e;
  };
}

/** Creates a passkey: for an invite (new account or recovery), or for the signed-in account. */
export async function createPasskey(request: RegisterOptionsRequest): Promise<User> {
  const { ceremonyId, options } = await api('/api/auth/register/options', CeremonyOptions, { body: request });
  const response = await startRegistration({
    optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON,
  }).catch(reportFailure('register'));
  return (await api('/api/auth/register/verify', UserResponse, { body: { ceremonyId, response } })).user;
}

export async function signInWithPasskey(): Promise<User> {
  const { ceremonyId, options } = await api('/api/auth/login/options', CeremonyOptions, { body: {} });
  const response = await startAuthentication({
    optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
  }).catch(reportFailure('login'));
  try {
    return (await api('/api/auth/login/verify', UserResponse, { body: { ceremonyId, response } })).user;
  } catch (e) {
    // Tell the password manager this passkey is dead, so it stops offering it.
    if (e instanceof ApiError && e.code === 'unknown_passkey') {
      void sendSignal({ signalName: 'unknownCredential', rpID: location.hostname, credentialID: response.id }).catch(() => {});
    }
    throw e;
  }
}

/** A message for a failed passkey attempt. */
export function passkeyErrorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof WebAuthnError) {
    if (e.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') {
      return 'This device or password manager already has a passkey for this account.';
    }
    if (e.code === 'ERROR_CEREMONY_ABORTED') return 'Cancelled.';
    if ((e.cause as { name?: string } | undefined)?.name === 'NotAllowedError') {
      return 'The passkey prompt was closed or timed out. Try again when you’re ready.';
    }
    return e.message;
  }
  return 'Something went wrong with the passkey. Please try again.';
}
